import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment';
import { TaskState, TaskConversationState, ITask } from '@nimbus-assistant/models/task.model';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { BannerPriority, getBannerPriority } from '@nimbus-assistant/helpers/task-priority.helpers';
import { StateBannerType, IStateBanner } from '@nimbus-assistant/interfaces/banner';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { UserNotReadyReason } from '@nimbus-library/features/user-state/models/not-ready-reason';
import { AssistantTasksService } from './assistant-tasks.service';
import { UserStateService, UserSelectableState } from './user-state.service';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';
import { LoggingService } from './logging.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class BannerService {
  public readonly banner$ = new BehaviorSubject<IStateBanner | null>(null);

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly userStateService: UserStateService,
    private readonly authService: AuthService,
    private readonly settingsService: SettingsService,
    private readonly loggingService: LoggingService,
  ) {
    this.setupSubscriptions();
  }

  private setupSubscriptions(): void {
    combineLatest([
      this.authService.authenticated$.pipe(distinctUntilChanged()),
      this.tasksService.connectedTask$,
      this.tasksService.acwTask$,
      this.tasksService.rona$,
      this.userStateService.selectableState$,
      this.userStateService.notReadyReason$,
      this.settingsService.settings$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isAuth, connected, acw, rona, selectableState, notReadyReason]) => {
        if (!isAuth) {
          this.banner$.next(null);
          return;
        }

        this.banner$.next(this.computeBanner(connected, acw, rona, selectableState, notReadyReason));
      });
  }

  private computeBanner(
    connected: ITask | null,
    acw: ITask | null,
    rona: { started: string } | null,
    selectableState: UserSelectableState,
    notReadyReason?: UserNotReadyReason,
  ): IStateBanner | null {
    if (!this.hasBannerContext()) {
      this.loggingService.Info('[BannerService] computeBanner: no banner context, clearing banner');
      return null;
    }

    const banner = this.buildBannerPriority(connected, acw, rona, selectableState);
    if (!banner) {
      return null;
    }

    banner.notReadyReason = notReadyReason;
    return banner;
  }

  private buildBannerPriority(
    connected: ITask | null,
    acw: ITask | null,
    rona: { started: string } | null,
    selectableState: UserSelectableState,
  ): IStateBanner | null {
    const priority = getBannerPriority(connected, acw, !!rona);
    this.loggingService.Info(
      `[BannerService] computeBanner: priority=${BannerPriority[priority]}, connected=${!!connected}, acw=${!!acw}, rona=${!!rona}, selectableState=${selectableState}`,
    );

    switch (priority) {
      case BannerPriority.Acw:
        return this.buildAcwBanner(acw);
      case BannerPriority.Rona:
        return this.buildRonaBanner(rona);
      case BannerPriority.Connected:
        return this.buildConnectedBanner(connected);
      case BannerPriority.Availability: {
        const banner = this.buildAvailabilityBanner(selectableState);
        this.loggingService.Info(
          `[BannerService] buildAvailabilityBanner result=${banner?.type ?? 'null'}, belongsToAnyTeam=${this.settingsService.settings$.value?.belongsToAnyTeam}, hasContactCenterLicense=${this.settingsService.settings$.value?.hasContactCenterLicense}`,
        );
        return banner;
      }
      default:
        return null;
    }
  }

  private buildConnectedBanner(task: ITask): IStateBanner {
    const isParked = task.conversationState === TaskConversationState.Parked;
    return {
      type: StateBannerType.InCall,
      text: this.getSessionTypeText(task.modality),
      cssClass: 'inCall',
      subtext: isParked ? TranslationKeys.CallParked : undefined,
      countUpFrom: !task.isDirectCall && task.acceptedAt ? moment(task.acceptedAt) : null,
    };
  }

  private buildAcwBanner(task: ITask): IStateBanner {
    const acw = task.afterCallWork;
    const isExtended = task.state === TaskState.InExtendedACW;
    const startedAt = acw?.started ? moment(acw.started) : null;
    const durationInSeconds = acw?.durationInSeconds ?? acw?.leftInSeconds ?? 0;
    // Anchor the countdown on the ACW start so a reload/reconnect keeps showing the real remaining time.
    const countDownTo = startedAt?.isValid()
      ? startedAt.clone().add(durationInSeconds, 'seconds')
      : moment().add(acw?.leftInSeconds ?? 0, 'seconds');

    const banner: IStateBanner = {
      type: StateBannerType.Acw,
      text: isExtended ? TranslationKeys.DutyExtendedAcw : TranslationKeys.DutyAcw,
      cssClass: 'acw',
      countDownTo,
      countDownLeftSeconds: durationInSeconds,
      allowToStop: acw?.allowToStop ?? false,
      stopHandler: (callBackend?: boolean) => this.userStateService.closeAcw(callBackend),
    };

    if (acw?.allowToExtend && !isExtended) {
      banner.allowToExtend = true;
      banner.extendHandler = (): void => this.userStateService.extendAcw();
    }

    return banner;
  }

  private buildRonaBanner(rona: { started: string }): IStateBanner {
    return {
      type: StateBannerType.Rona,
      text: TranslationKeys.GeneralRona,
      cssClass: 'rona',
      countUpFrom: rona.started ? moment(rona.started) : moment(),
      allowToStop: true,
      stopHandler: () => this.userStateService.resetRona(),
    };
  }

  private buildAvailabilityBanner(selectableState: UserSelectableState): IStateBanner | null {
    if (!this.settingsService.settings$.value?.belongsToAnyTeam && !this.settingsService.settings$.value?.hasContactCenterLicense) {
      return null;
    }

    if (selectableState === UserSelectableState.onDutySelectable) {
      return {
        type: StateBannerType.Available,
        text: TranslationKeys.GeneralReachabilityReady,
        cssClass: 'available',
      };
    }

    if (selectableState === UserSelectableState.onDutyNotSelectable || selectableState === UserSelectableState.offDuty) {
      return {
        type: StateBannerType.NotAvailable,
        text: TranslationKeys.GeneralReachabilityNotReady,
        cssClass: 'notAvailable',
      };
    }

    return null;
  }

  private getSessionTypeText(modality: Modality): TranslationKeys {
    switch (modality) {
      case Modality.IM:
        return TranslationKeys.CallInChat;
      case Modality.ExternalTask:
        return TranslationKeys.CallInExternalTask;
      case Modality.Email:
        return TranslationKeys.CallInEmail;
      case Modality.Audio:
      default:
        return TranslationKeys.CallInCall;
    }
  }

  private hasBannerContext(): boolean {
    return this.authService.authenticated$.value;
  }
}
